import { useTranslation } from "react-i18next"

import { RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

import { useFairUseConfigForm } from "./fair-use-config-form-context"
import { FairUseLeadAllowanceField } from "./fair-use-lead-allowance-field"
import { FairUsePeopleTable } from "./fair-use-people-table"
import { fairUseHasLeadAllowance, fairUseLeadAllowanceUpdate } from "./fair-use-settings-utils"
import { LocalAiLeadSettingsSection } from "../local-ai-lead-settings-section"
import { LocalAiLeadsManagementCard } from "../local-ai-leads-management-card"

/**
 * The lead approval allowance, as its own saveable card.
 *
 * It is a field on the FAIR-USE config document, while the roster card above it
 * saves the LEAD config document — two documents on one tab. Each therefore
 * keeps its own Save, named after what it saves, so nobody presses one
 * expecting the other: the hash-OCC contract is per document, and a single
 * shared button would have to guess which one the admin meant.
 *
 * The Save sends the allowance and NOTHING else
 * (`fairUseLeadAllowanceUpdate`). The thresholds and the fallback model are
 * on the same document and the same shared form, so a payload carrying them
 * would persist whatever the Settings tab had half-edited and abandoned —
 * from a button labelled for the lead allowance, with that tab's own Save
 * left armed as if nothing had happened. It is a panel of its own to the
 * provider (`leadAllowance`) for the same reason: pressing it clears only its
 * own dirty flag, and reads back only its own field.
 */
function FairUseLeadAllowanceCard() {
  const { t } = useTranslation()
  const { data, dirty, form, save, savingPanel, updateForm } = useFairUseConfigForm()

  // Absent on a round-1 backend, where sending the field would answer 400
  // `invalid_config` and take the four thresholds down with it.
  if (!data || !form || !fairUseHasLeadAllowance(data.config)) return null

  const updates = fairUseLeadAllowanceUpdate(form)
  const isSaving = savingPanel === "leadAllowance"

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-lg">
              {t("adminDashboard.fairUse.users.leadAllowanceTitle", "What a lead may approve")}
            </CardTitle>
            <p className="text-muted-foreground max-w-prose text-sm">
              {t(
                "adminDashboard.fairUse.users.leadAllowanceCaption",
                "Part of the fair use settings, saved on its own here."
              )}
            </p>
          </div>
          <Button
            disabled={!dirty.leadAllowance || updates === null || isSaving}
            onClick={() => save("leadAllowance", updates)}
          >
            {isSaving ? <RefreshCw className="mr-2 size-4 animate-spin" /> : null}
            {t("adminDashboard.fairUse.users.saveLeadAllowance", "Save lead allowance")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <FairUseLeadAllowanceField
          value={form.leadAllowanceSek}
          onChange={(leadAllowanceSek) => updateForm("leadAllowance", { leadAllowanceSek })}
        />
      </CardContent>
    </Card>
  )
}

/** The people table is a task of its own; roster and cost configuration live elsewhere. */
export function FairUseTabUsers() {
  const { t } = useTranslation()
  return (
    <Card className="rounded-lg border shadow-none">
      <CardHeader>
        <CardTitle className="text-lg">
          {t("adminDashboard.fairUse.users.title", "Users & licences")}
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.fairUse.users.description",
            "Who is on which plan, who their Local AI Lead is, and what they have spent this month."
          )}
        </p>
      </CardHeader>
      <CardContent>
        <FairUsePeopleTable />
      </CardContent>
    </Card>
  )
}

/** Assignment settings, lead roster, and the lead approval allowance. */
export function FairUseTabLeads() {
  const { t } = useTranslation()
  const { isPlatformAdmin } = useFairUseConfigForm()
  return (
    <div className="space-y-5">
      <Card className="rounded-lg border shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">
            {t("adminDashboard.fairUse.leads.assignmentTitle", "Lead assignments")}
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            {t(
              "adminDashboard.fairUse.leads.assignmentDescription",
              "Choose how people are grouped and which directory group may use this workspace."
            )}
          </p>
        </CardHeader>
        <CardContent>
          <LocalAiLeadSettingsSection canEditCosts={isPlatformAdmin} section="assignment" />
        </CardContent>
      </Card>
      <LocalAiLeadsManagementCard canEditCosts={isPlatformAdmin} showSettings={false} />
      <FairUseLeadAllowanceCard />
    </div>
  )
}

/** Cost assumptions and visible sections of the Local AI Lead dashboard. */
export function FairUseTabCosts() {
  const { t } = useTranslation()
  const { isPlatformAdmin } = useFairUseConfigForm()
  return (
    <Card className="rounded-lg border shadow-none">
      <CardHeader>
        <CardTitle className="text-lg">
          {t("adminDashboard.fairUse.leads.costTitle", "Cost assumptions & dashboard")}
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.fairUse.leads.costDescription",
            "Set shared cost assumptions and choose what Local AI Leads see."
          )}
        </p>
      </CardHeader>
      <CardContent>
        <LocalAiLeadSettingsSection canEditCosts={isPlatformAdmin} section="costs" />
      </CardContent>
    </Card>
  )
}
